import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFlightSearch } from "../../hooks/useFlightSearch";
import ResultsHeader from "./ResultsHeader";
import FlightResults from "./FlightResults";
import LoadingScreen from "./LoadingScreen";

const FlightResultsPage: React.FC = () => {
    const navigate = useNavigate();
    const { searchData, isLoading, hasResults } = useFlightSearch();

    useEffect(() => {
        // If no search data, redirect to booking page
        if (!isLoading && !hasResults) {
            navigate("/philippine-airlines", { replace: true });
        }
    }, [isLoading, hasResults, navigate]);

    // Show loading while checking sessionStorage
    if (isLoading) {
        return <LoadingScreen />;
    }

    // No data available (shouldn't happen due to redirect, but safety check)
    if (!searchData || !searchData.searchResults) {
        return null;
    }

    // Parse the search date for display
    const getSearchDate = () => {
        const { searchParams } = searchData;
        // Assuming departureDate is in "15-Sep-2025" format from form
        // Convert to ISO format for FlightResults component
        if (searchParams.departureDate) {
            const parts = searchParams.departureDate.split("-");
            if (parts.length === 3) {
                const monthMap: { [key: string]: string } = {
                    Jan: "01",
                    Feb: "02",
                    Mar: "03",
                    Apr: "04",
                    May: "05",
                    Jun: "06",
                    Jul: "07",
                    Aug: "08",
                    Sep: "09",
                    Oct: "10",
                    Nov: "11",
                    Dec: "12",
                };
                const day = parts[0].padStart(2, "0");
                const month = monthMap[parts[1]] || "01";
                const year = parts[2];
                return `${year}-${month}-${day}`;
            }
        }
        // Fallback to today's date
        return new Date().toISOString().split("T")[0];
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Blue background container */}
            <div className="bg-[#0047ba] relative">
                <ResultsHeader
                    origin={searchData.searchParams.origin}
                    destination={searchData.searchParams.destination}
                    departureDate={searchData.searchParams.departureDate}
                    returnDate={searchData.searchParams.returnDate}
                    passengers={
                        searchData.searchParams.adults +
                        (searchData.searchParams.children || 0) +
                        (searchData.searchParams.infants || 0)
                    }
                    onModify={() => {
                        navigate("/philippine-airlines");
                    }}
                />
                {/* This padding creates space for the DatePriceGrid to overlap */}
                {/* <div style={{ paddingBottom: "100px" }}></div> */}
            </div>

            {/* DatePriceGrid positioned to overlap the blue background */}
            <div>
                <FlightResults
                    flights={searchData.searchResults}
                    tripType={
                        searchData.searchParams.tripType as
                            | "one-way"
                            | "round-trip"
                            | "multi-city"
                    }
                    searchDate={getSearchDate()}
                    origin={searchData.searchParams.origin}
                    destination={searchData.searchParams.destination}
                    returnDate={
                        searchData.searchParams.returnDate
                            ? (() => {
                                  const parts =
                                      searchData.searchParams.returnDate.split(
                                          "-"
                                      );
                                  if (parts.length === 3) {
                                      const monthMap: {
                                          [key: string]: string;
                                      } = {
                                          Jan: "01",
                                          Feb: "02",
                                          Mar: "03",
                                          Apr: "04",
                                          May: "05",
                                          Jun: "06",
                                          Jul: "07",
                                          Aug: "08",
                                          Sep: "09",
                                          Oct: "10",
                                          Nov: "11",
                                          Dec: "12",
                                      };
                                      const day = parts[0].padStart(2, "0");
                                      const month = monthMap[parts[1]] || "01";
                                      const year = parts[2];
                                      return `${year}-${month}-${day}`;
                                  }
                                  return undefined;
                              })()
                            : undefined
                    }
                    dictionaries={searchData.dictionaries}
                />
            </div>
        </div>
    );
};

export default FlightResultsPage;
